import { Alert, Box, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getGrowthExperiments, getGrowthFunnel, getTrialPipeline } from 'API/engagement';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExperimentVariantRow, FunnelRow, GrowthFunnel, Metric } from 'types/engagement.types';
import { HBarList } from '../../shared/charts';
import { fmtEuroCents, fmtInt, fmtPct } from '../../shared/format';
import { MetricText } from '../../shared/metric';
import { KpiRow, KpiTile, QueryState, SectionCard } from '../../shared/ui';
import { experimentDecisionText, totalCostPerCustomer } from '../engagement-logic';

const sum = (rows: FunnelRow[], k: 'registrations' | 'activated' | 'firstReply' | 'purchases') => rows.reduce((a, r) => a + (r[k] ?? 0), 0);

/** Stripe revenue vs. the sum over the sources — `{ revenueEventsCents, attributedCents, differenceCents, ok, reason? }`. */
const ControlSumNotice: React.FC<{ controlSum: GrowthFunnel['controlSum'] }> = ({ controlSum }) => {
  const { t, i18n } = useTranslation();
  if (!controlSum) return null;
  if (controlSum.ok) return <Alert severity={'success'}>{t('engControlSumOk')}</Alert>;
  return (
    <Alert severity={'warning'}>
      {t('engControlSumNotOk')} {controlSum.reason ?? ''} ({fmtEuroCents(i18n.language)(controlSum.differenceCents)})
    </Alert>
  );
};

export const ExperimentsTable: React.FC<{ rows: ExperimentVariantRow[] }> = ({ rows }) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const groups = new Map<string, ExperimentVariantRow[]>();
  rows.forEach((r) => groups.set(r.experimentId, [...(groups.get(r.experimentId) ?? []), r]));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size={'small'}>
        <TableHead>
          <TableRow>
            <TableCell>{t('engColExperiment')}</TableCell>
            <TableCell align={'right'}>{t('engColViews')}</TableCell>
            <TableCell align={'right'}>{t('engColCtaRate')}</TableCell>
            <TableCell align={'right'}>{t('engColRegistrations')}</TableCell>
            <TableCell align={'right'}>{t('engColActivated')}</TableCell>
            <TableCell align={'right'}>{t('engColPurchases')}</TableCell>
            <TableCell>{t('engColDecision')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from(groups.entries()).flatMap(([id, variants]) => {
            const decided = variants.find((v) => v.decision === 'winner');
            const statusRow = decided ?? variants[0];
            return variants.map((v, i) => (
              <TableRow key={`${id}-${v.variant}`}>
                <TableCell>
                  {i === 0 ? <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{v.name ?? id}</Typography> : null}
                  <Typography sx={{ fontSize: 12 }}>
                    {v.variant}
                    {v.variantLabel ? ` „${v.variantLabel}“` : ''}
                  </Typography>
                </TableCell>
                <TableCell align={'right'}>{fmtInt(lng)(v.views)}</TableCell>
                <TableCell align={'right'}>{v.ctaRate === null ? '—' : fmtPct(lng, 1)(v.ctaRate)}</TableCell>
                <TableCell align={'right'}>{v.registrations}</TableCell>
                <TableCell align={'right'}>{v.activated}</TableCell>
                <TableCell align={'right'}>{v.purchases}</TableCell>
                <TableCell>
                  {i === 0 ? (
                    <Chip
                      size={'small'}
                      color={statusRow.decision === 'winner' ? 'success' : 'default'}
                      variant={statusRow.decision === 'winner' ? 'filled' : 'outlined'}
                      label={experimentDecisionText(statusRow, t, lng)}
                      data-testid={`experiment-decision-${id}`}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            ));
          })}
        </TableBody>
      </Table>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>{t('engExpNote')}</Typography>
    </Box>
  );
};

export const FunnelTable: React.FC<{ rows: FunnelRow[] }> = ({ rows }) => {
  const { t, i18n } = useTranslation();
  const euro = fmtEuroCents(i18n.language);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size={'small'}>
        <TableHead>
          <TableRow>
            <TableCell>{t('engColSource')}</TableCell>
            <TableCell align={'right'}>{t('engColSpend')}</TableCell>
            <TableCell align={'right'}>{t('engColRegistrations')}</TableCell>
            <TableCell align={'right'}>{t('engColActivated')}</TableCell>
            <TableCell align={'right'}>{t('engColFirstReply')}</TableCell>
            <TableCell align={'right'}>{t('engColPurchases')}</TableCell>
            <TableCell align={'right'}>{t('engColCostPerCustomer')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.source}>
              <TableCell>{r.source === 'unattributed' ? t('engSourceUnattributed') : r.source === 'direct' ? t('engSourceDirect') : r.source}</TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.spend} format={euro} />
              </TableCell>
              <TableCell align={'right'}>{r.registrations}</TableCell>
              <TableCell align={'right'}>{r.activated}</TableCell>
              <TableCell align={'right'}>{r.firstReply}</TableCell>
              <TableCell align={'right'}>{r.purchases}</TableCell>
              <TableCell align={'right'}>
                <MetricText value={r.costPerCustomer} format={euro} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const TrialPipeline: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [dayFrom, setDayFrom] = useState(20);
  const [minReplies, setMinReplies] = useState(0);
  const q = useQuery({ queryKey: ['engagement', 'trial-pipeline', dayFrom, minReplies], queryFn: () => getTrialPipeline({ dayFrom, minReplies }) });
  const rows = q.data?.items ?? [];
  const cell = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'boolean' ? (v ? '✓' : '—') : String(v));
  return (
    <SectionCard title={t('engTrialPipeline')}>
      <Stack direction={'row'} gap={1.5} sx={{ mb: 1.5 }}>
        <TextField size={'small'} type={'number'} label={t('engAudienceTrialFrom')} value={dayFrom} onChange={(e) => setDayFrom(Number(e.target.value) || 0)} />
        <TextField size={'small'} type={'number'} label={t('engAudienceMinReplies')} value={minReplies} onChange={(e) => setMinReplies(Number(e.target.value) || 0)} />
      </Stack>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyText={t('engTrialEmpty')}>
        <Table size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell>{t('engColTenant')}</TableCell>
              <TableCell align={'right'}>{t('engColTrialDay')}</TableCell>
              <TableCell>{t('engColState')}</TableCell>
              <TableCell align={'right'}>{t('engColReplies')}</TableCell>
              <TableCell>{t('engColConsent')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={r.tenantId || i}>
                <TableCell>{cell(r.name || r.tenantId)}</TableCell>
                <TableCell align={'right'}>{r.day === null ? '—' : r.lengthDays ? `${r.day} / ${r.lengthDays}` : String(r.day)}</TableCell>
                <TableCell>{t(`engTrialStage_${r.stage}`, { defaultValue: r.stage })}</TableCell>
                <TableCell align={'right'}>
                  {r.results && r.results.status && r.results.status !== 'ok' ? (
                    <MetricText value={r.results as Metric} format={fmtInt(i18n.language)} />
                  ) : (
                    cell(r.results?.replies)
                  )}
                </TableCell>
                <TableCell>{cell(r.marketingConsent)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueryState>
    </SectionCard>
  );
};

const GrowthTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const funnel = useQuery({ queryKey: ['engagement', 'growth', 'funnel', 30], queryFn: () => getGrowthFunnel(30) });
  const experiments = useQuery({ queryKey: ['engagement', 'growth', 'experiments'], queryFn: getGrowthExperiments });

  const rows = funnel.data?.rows ?? [];
  const reg = sum(rows, 'registrations');
  const ratio = (n: number) => (reg > 0 ? fmtPct(lng)(n / reg) : '—');

  return (
    <Stack gap={2.5}>
      <QueryState isLoading={funnel.isLoading} error={funnel.error} isEmpty={rows.length === 0} emptyText={t('engFunnelEmpty')}>
        <KpiRow>
          <KpiTile label={t('engKpiRegistrations')} value={fmtInt(lng)(reg)} />
          <KpiTile label={t('engKpiActivated')} value={ratio(sum(rows, 'activated'))} sub={t('engKpiActivatedSub')} />
          <KpiTile label={t('engKpiFirstReply')} value={ratio(sum(rows, 'firstReply'))} sub={t('engKpiFirstReplySub')} />
          <KpiTile label={t('engKpiTrialToPurchase')} value={ratio(sum(rows, 'purchases'))} sub={t('engKpiPurchasesCount', { count: sum(rows, 'purchases') })} />
          <KpiTile label={t('engKpiCostPerCustomer')} value={<MetricText value={totalCostPerCustomer(rows)} format={fmtEuroCents(lng)} variant={'kpi'} />} sub={t('engKpiCostPerCustomerSub')} />
        </KpiRow>
        <SectionCard title={t('engFunnelTitle')}>
          <FunnelTable rows={rows} />
          <Box sx={{ mt: 1.5 }}>
            <ControlSumNotice controlSum={funnel.data?.controlSum} />
          </Box>
        </SectionCard>
        <SectionCard title={t('engTrialFunnelTitle', { count: reg })}>
          <HBarList
            rows={[
              { label: t('engColRegistrations'), value: reg },
              { label: t('engColActivated'), value: sum(rows, 'activated') },
              { label: t('engColFirstReply'), value: sum(rows, 'firstReply') },
              { label: t('engColPurchases'), value: sum(rows, 'purchases') },
            ]}
          />
        </SectionCard>
      </QueryState>
      <TrialPipeline />
      <SectionCard title={t('engExperimentsTitle')}>
        <QueryState
          isLoading={experiments.isLoading}
          error={experiments.error}
          isEmpty={(experiments.data ?? []).length === 0}
          emptyText={t('engExperimentsEmpty')}
        >
          <ExperimentsTable rows={experiments.data ?? []} />
        </QueryState>
      </SectionCard>
    </Stack>
  );
};

export default GrowthTab;
